/**
 * Zustand store for the project activity log.
 *
 * Entries are bucketed by projectId — each project holds up to MAX_ENTRIES
 * recent entries (newest first). Entries push in real-time via WebSocket
 * and pre-load from the REST API when the project is opened.
 */

import { create } from "zustand";
import { mirroredStore } from "@/lib/mirrored-store";
import { stripCursorDisplayTokens } from "@/lib/cursor-token-display";
import * as activityApi from "@/api/activity";
import type { ActivityLogEntry } from "@/api/activity";
import { captureDesktopChatAuthScopeGuard } from "@/desktop/desktop-chat-auth-scope";
import { translate } from "@/i18n";
import { storesNoticesMessages } from "@/i18n/messages/stores-notices";

const MAX_ENTRIES = 50;

/** Color assigned to each actor type for the dot indicator. */
export const ACTOR_DOT_COLORS: Record<string, string> = {
  agent: "var(--color-accent)",
  user: "#007AFF",
  system: "var(--color-muted)",
};

export interface ActivityEntry {
  /** Stable unique ID (UUID from backend or generated client-side). */
  id: string;
  /** Human-readable description, e.g. "Coder edited auth-service.py". */
  description: string;
  /** CSS color string for the dot indicator. */
  dotColor: string;
  /** ISO 8601 timestamp string. */
  createdAt: string;
  /** Actor type for badge rendering: "user" | "agent" | "system" */
  actorType: string;
  /** Project ID this entry belongs to. */
  projectId: string;
  /** Task ID if this activity relates to a task. */
  taskId?: string;
  /** Display string like "MC-3" for ticket badge. */
  taskTicket?: string;
  /** Frame ID if this activity created/modified a frame. */
  frameId?: string;
  /** Frame name for clickable link display. */
  frameName?: string;
  /** Whether this is a task lifecycle event (for green tint styling). */
  isTaskLifecycle?: boolean;
  /** Execution source: "chat" | "heartbeat" | "delegation". */
  source?: string;
}

/** Map backend wake_reason to a human-readable source label. */
const WAKE_REASON_TO_SOURCE: Record<string, string> = {
  user_prompt: "chat",
  heartbeat: "heartbeat",
  delegation: "delegation",
};

/**
 * Convert a backend ActivityLogEntry into a display-ready ActivityEntry.
 *
 * Turns dot-namespaced actions like "task.created" into human-readable
 * descriptions like "Task created". Falls back to the raw action string.
 */
export function toActivityEntry(log: ActivityLogEntry): ActivityEntry {
  const details = log.details as Record<string, unknown>;
  const projectId = log.project;

  // Compute task/frame fields once — applies to all code paths
  const taskId = log.task ?? undefined;
  const taskTicket = log.task_ticket ?? undefined;
  const isTaskLifecycle = log.action.startsWith("task.");
  const frameId = log.target_type === "frame" ? log.target_id : undefined;
  const frameName =
    log.target_type === "frame"
      ? ((log.details.title as string) ?? undefined)
      : undefined;

  // Resolve execution source from wake_reason (for source badge display)
  const wakeReason = details?.wake_reason as string | undefined;
  const source = wakeReason ? WAKE_REASON_TO_SOURCE[wakeReason] : undefined;

  // User prompt actions — use details.description directly
  if (log.action === "user.prompt") {
    return {
      id: log.id,
      description:
        stripCursorDisplayTokens((details?.description as string) || "") ||
        translate(storesNoticesMessages, "sentMessage"),
      dotColor: "#007AFF",
      createdAt: log.created_at,
      actorType: "user",
      projectId,
      taskId,
      taskTicket,
      frameId,
      frameName,
      isTaskLifecycle,
      source,
    };
  }

  // Agent stream actions — use details.description directly
  if (log.action.startsWith("agent.") && details?.description) {
    const agentName = details.agent_name as string | undefined;
    const displayDescription = stripCursorDisplayTokens(
      details.description as string,
    );
    const desc = agentName
      ? `${agentName}: ${displayDescription}`
      : displayDescription;
    return {
      id: log.id,
      description: desc,
      dotColor: ACTOR_DOT_COLORS[log.actor_type] ?? "var(--color-muted)",
      createdAt: log.created_at,
      actorType: log.actor_type,
      projectId,
      taskId,
      taskTicket,
      frameId,
      frameName,
      isTaskLifecycle,
      source,
    };
  }

  // Legacy action types (task.created, execution.completed, etc.)
  const verb = log.action.includes(".")
    ? log.action.split(".").pop()!
    : log.action;
  const noun =
    log.target_type.charAt(0).toUpperCase() + log.target_type.slice(1);
  const name = details?.name ?? details?.title;
  const description = name ? `${noun} ${verb}: ${name}` : `${noun} ${verb}`;

  return {
    id: log.id,
    description,
    dotColor: ACTOR_DOT_COLORS[log.actor_type] ?? "var(--color-muted)",
    createdAt: log.created_at,
    actorType: log.actor_type,
    projectId,
    taskId,
    taskTicket,
    frameId,
    frameName,
    isTaskLifecycle,
    source,
  };
}

interface ActivityState {
  /** Per-project entry buckets (newest first). Each bucket capped at MAX_ENTRIES. */
  entriesByProject: Record<string, ActivityEntry[]>;

  /** Entry ID to scroll to and highlight in the history tab. */
  highlightedEntryId: string | null;
  /** Search query to highlight within the targeted entry. */
  highlightQuery: string | null;

  /** Fetch project activity from the REST API. */
  fetchActivity: (projectId: string) => Promise<void>;

  /** Prepend a single new entry; routes to its project bucket. */
  pushEntry: (entry: ActivityEntry) => void;

  /** Read entries for one project (newest-first). Empty array if none. */
  getEntriesForProject: (projectId: string) => ActivityEntry[];

  /** Scroll to and briefly highlight an entry, with optional text highlight. */
  highlightEntry: (entryId: string, query?: string) => void;

  /** Clear all buckets (e.g. on project change). */
  clear: () => void;
}

export const useActivityStore = create<ActivityState>()(
  mirroredStore("activity", (set, get) => ({
    entriesByProject: {},
    highlightedEntryId: null,
    highlightQuery: null,

    highlightEntry: (entryId, query) => {
      set({ highlightedEntryId: entryId, highlightQuery: query ?? null });
      setTimeout(
        () => set({ highlightedEntryId: null, highlightQuery: null }),
        5000,
      );
    },

    fetchActivity: async (projectId) => {
      const isCurrentAuthScope = captureDesktopChatAuthScopeGuard();
      try {
        const logs = await activityApi.listActivity(projectId, {
          limit: MAX_ENTRIES,
        });
        if (!isCurrentAuthScope()) return;
        set((state) => ({
          entriesByProject: {
            ...state.entriesByProject,
            [projectId]: logs.map(toActivityEntry),
          },
        }));
      } catch {
        // Activity is non-critical — fail silently
      }
    },

    pushEntry: (entry) =>
      set((state) => {
        const key = entry.projectId;
        const bucket = state.entriesByProject[key] ?? [];
        // Dedup: skip if an entry with this id is already in the bucket.
        // Prevents duplicates when a WS activity.created event arrives for
        // an entry that was already loaded via fetchActivity (REST + WS race).
        if (bucket.some((e) => e.id === entry.id)) return state;
        return {
          entriesByProject: {
            ...state.entriesByProject,
            [key]: [entry, ...bucket].slice(0, MAX_ENTRIES),
          },
        };
      }),

    getEntriesForProject: (projectId) =>
      get().entriesByProject[projectId] ?? [],

    clear: () => set({ entriesByProject: {} }),
  })),
);
